import { selectCurrencyCode } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectSalesPriceDiscounts } from '@repo/api-adapters/selectors/price.selectors';
import type { DtoOrderDiscount } from '@repo/api-types/order-retrieval';
import { translate } from '@repo/phrase';
import type { SalesPersonDiscount } from '@repo/types/sales-price';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { formatPrice } from '@/utils/translation.utils';

/**
 * Extracts **only** discounts manually applied by sales person.
 * @returns List and sum of discounts
 */
export const useSalesPersonDiscounts = () => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const discounts = selectSalesPriceDiscounts(data);
    const currencyCode = selectCurrencyCode(data);

    const discountListItems = discounts
        .filter((discount): discount is SalesPersonDiscount & { discountType: string } =>
            Boolean(discount.discountType)
        )
        .map((discount) => {
            const normalize = discountListItemPropsNormalizerMap[discount.discountType];

            if (!normalize) {
                return null;
            }

            return normalize(discount, currencyCode ?? '');
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item));

    const discountListTotal = discountListItems.reduce((acc, curr) => acc + curr.discountAmount, 0);

    return { discountListItems, discountListTotal };
};

const normalizeManualDiscount = (discount: DtoOrderDiscount, currencyCode: string) => {
    const discountValue = discount.discountCalcMethod === 'PERCENTAGE' ? `${discount.percentage}%` : '';

    return {
        description: undefined, // TODO: Currently discount reason is not provided by API
        discountAmount: discount.amount ?? 0,
        quantityLabel: `- ${formatPrice(discount.amount, currencyCode)}`,
        title: `${translate('asset_discount_manual')}: ${discountValue}`,
    };
};

const normalizeEmployeeDiscount = (discount: SalesPersonDiscount, currencyCode: string) => {
    const discountValue = discount.discountCalcMethod === 'PERCENTAGE' ? `${discount.percentage}%` : '';
    const employeeCompany = discount.employeeCompanyName
        ? `, ${translate('employee_company')}: ${discount.employeeCompanyName}`
        : '';

    return {
        description: `${translate('employee_id')}: ${discount.referenceId}${employeeCompany}`,
        discountAmount: discount.amount ?? 0,
        quantityLabel: `- ${formatPrice(discount.amount, currencyCode)}`,
        title: `${translate('asset_discount_employee')}: ${discountValue}`,
    };
};

const normalizeCouponDiscount = (discount: DtoOrderDiscount, currencyCode: string) => {
    return {
        description: undefined, // TODO: Currently coupon description is not provided by API
        discountAmount: discount.amount ?? 0,
        quantityLabel: `- ${formatPrice(discount.amount, currencyCode)}`,
        title: `${translate('asset_discount_coupon')}: ${discount.referenceId}`,
    };
};

const discountListItemPropsNormalizerMap: Record<
    string,
    (
        discount: SalesPersonDiscount,
        currencyCode: string
    ) => { description: string | undefined; discountAmount: number; title: string; quantityLabel: string }
> = {
    MANUAL: normalizeManualDiscount,
    EMPLOYEE: normalizeEmployeeDiscount,
    COUPON: normalizeCouponDiscount,
};
